import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

const PublisherAuthContext = createContext(null);

export const PublisherAuthProvider = ({ children }) => {
  const [publisherUser, setPublisherUser] = useState(null);
  const [isPublisherAuthenticated, setIsPublisherAuthenticated] = useState(false);
  const [isPublisherLoading, setIsPublisherLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for an existing publisher session when the app loads
    const checkPublisherSession = async () => {
      try {
        // We need a new endpoint to check publisher session, or we can try to get the dashboard data
        const response = await api.get('/publisher-dashboard/me');
        if (response.data) {
          // The response from /me is the Publisher entity, not the PublisherUser.
          // For now, we'll just set authenticated status. A more robust solution
          // might have a /publisher-dashboard/me/profile endpoint that returns the user.
          setPublisherUser(response.data); // Storing publisher data as the user object
          setIsPublisherAuthenticated(true);
        }
      } catch (error) {
        if (error.response && error.response.status !== 401) {
          console.error('Failed to fetch publisher session:', error);
        }
        setPublisherUser(null);
        setIsPublisherAuthenticated(false);
      } finally {
        setIsPublisherLoading(false);
      }
    };

    checkPublisherSession();
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/publisher/login', { email, password });
    if (response.data.success) {
      setPublisherUser(response.data.user);
      setIsPublisherAuthenticated(true);
      navigate('/publisher/dashboard');
    }
    return response.data;
  };

  const logout = async () => {
    await api.post('/auth/publisher/logout');
    setPublisherUser(null);
    setIsPublisherAuthenticated(false);
    navigate('/publisher/login');
  };

  const value = { publisherUser, isPublisherAuthenticated, isPublisherLoading, login, logout };

  return <PublisherAuthContext.Provider value={value}>{children}</PublisherAuthContext.Provider>;
};

export const usePublisherAuth = () => {
  return useContext(PublisherAuthContext);
};